import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success(data.message || "Password changed");
      nav("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-5 py-10">
      <div className="w-full max-w-md fade-up" data-testid="reset-page">
        <div className="mb-6 flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500">
            <ShieldCheck className="h-5 w-5 text-white" />
          </div>
          <p className="font-display text-lg font-extrabold tracking-tight text-slate-900">ARPS</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Choose a new password</h1>
          <p className="mt-1.5 text-sm text-slate-500">Your new password must be at least 10 characters and include a letter and a number.</p>
          {!token ? (
            <div className="mt-5 rounded-lg bg-red-50 p-4 text-sm text-red-700" data-testid="reset-invalid">
              This reset link is missing its token. Please request a new link from the sign-in page.
            </div>
          ) : (
            <form onSubmit={submit} className="mt-5 space-y-4">
              <div>
                <Label htmlFor="np">New password</Label>
                <PasswordInput data-testid="reset-password-input" id="np" required value={pw} onChange={(e) => setPw(e.target.value)} placeholder="••••••••••" />
              </div>
              <div>
                <Label htmlFor="cp">Confirm new password</Label>
                <PasswordInput data-testid="reset-confirm-input" id="cp" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••••" />
              </div>
              <Button data-testid="reset-submit-button" type="submit" disabled={busy} className="w-full bg-slate-900 text-white hover:bg-slate-800">
                {busy ? "Updating…" : "Change password"}
              </Button>
            </form>
          )}
          <button data-testid="reset-back-to-login" onClick={() => nav("/login")} className="mt-5 w-full text-center text-sm font-semibold text-emerald-600 hover:underline">
            ← Back to sign in
          </button>
        </div>
      </div>
    </div>
  );
}
